/**
 * CreatePostModal — Facebook-style "Create post" with media upload,
 * VinylSubmit, EmojiPicker, and "Add to your post" action bar.
 * File picker uses a hidden <input type="file"> inside the modal to
 * avoid z-index stacking issues with the modal backdrop.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Smile, ImagePlay, UserRoundPlus, MapPin, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useAuthStore } from '@/features/auth';
import { useUpload } from '@/features/messaging/hooks';
import { EmojiPicker } from '@/features/messaging/components/EmojiPicker';
import { FileTypeIcon } from '@/features/messaging/components/FileTypeIcon';
import { UserAvatar } from '@/components/ui';
import { useCreatePost, useUpdatePost } from '../hooks';
import { STATUS_GRADIENTS, getGradientById, gradientCSS } from '../constants';
import { TagPeopleModal } from './TagPeopleModal';
import type { FeedPost, TaggableUser } from '../types';

const MAX_CONTENT_LENGTH = 5000;
const BG_SHORT_THRESHOLD = 85;

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  editPost?: FeedPost;
}

interface UploadedMedia {
  url: string;
  type: 'image' | 'video' | 'document';
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

export function CreatePostModal({ isOpen, onClose, editPost }: CreatePostModalProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();
  const { isUploading, progress, upload, reset: resetUpload } = useUpload();

  const isEditing = !!editPost;
  const isPending = createPost.isPending || updatePost.isPending;

  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'FEED',
  });

  const [content, setContent] = useState('');
  const [media, setMedia] = useState<UploadedMedia | null>(null);
  const [selectedBg, setSelectedBg] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState<TaggableUser[]>([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const firstName = user?.firstName ?? '';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  // Populate form when editing
  useEffect(() => {
    if (editPost && isOpen) {
      setContent(editPost.content);
      setSelectedBg(editPost.bgGradient ?? null);
      setTaggedUsers(editPost.taggedUsers ?? []);
      if (editPost.mediaUrl) {
        setMedia({
          url: editPost.mediaUrl,
          type: editPost.mediaType ?? 'image',
        });
      } else {
        setMedia(null);
      }
      setError('');
    } else if (isOpen) {
      setContent('');
      setSelectedBg(null);
      setMedia(null);
      setTaggedUsers([]);
      setError('');
      resetUpload();
    }
  }, [editPost, isOpen, resetUpload]);

  const isFormValid = useMemo(
    () => content.trim().length > 0 && content.length <= MAX_CONTENT_LENGTH,
    [content],
  );

  const validateForm = useCallback((): boolean => {
    if (!content.trim()) {
      setError(t('feed.error_content_required'));
      return false;
    }
    if (content.length > MAX_CONTENT_LENGTH) {
      setError(t('feed.error_content_too_long'));
      return false;
    }
    setError('');
    return true;
  }, [content, t]);

  // Show background picker only for short, text-only posts (no media, no links)
  const canShowBgPicker = content.trim().length > 0
    && content.length < BG_SHORT_THRESHOLD
    && !content.includes('http')
    && !media;

  // Clear background selection when it becomes invalid
  useEffect(() => {
    if (!canShowBgPicker && selectedBg) {
      setSelectedBg(null);
    }
  }, [canShowBgPicker, selectedBg]);

  const handleSubmit = useCallback(() => {
    submitWithAnimation({
      validate: validateForm,
      mutate: async () => {
        const tagIds = taggedUsers.length > 0 ? taggedUsers.map((u) => u.id) : undefined;
        if (isEditing && editPost) {
          await updatePost.mutateAsync({
            id: editPost.id,
            data: {
              content: content.trim(),
              mediaUrl: media?.url ?? null,
              mediaType: media?.type ?? null,
              bgGradient: canShowBgPicker ? (selectedBg ?? null) : null,
              taggedUserIds: tagIds ?? [],
            },
          });
        } else {
          await createPost.mutateAsync({
            content: content.trim(),
            ...(media
              ? { mediaUrl: media.url, mediaType: media.type }
              : {}),
            ...(canShowBgPicker && selectedBg
              ? { bgGradient: selectedBg }
              : {}),
            ...(tagIds ? { taggedUserIds: tagIds } : {}),
          });
        }
      },
      onSuccess: () => {
        toast.success(
          isEditing ? t('feed.postUpdated') : t('feed.postCreated'),
        );
        onClose();
      },
      onError: () => {
        setError(t('feed.error_submit_failed'));
      },
    });
  }, [
    submitWithAnimation,
    validateForm,
    isEditing,
    editPost,
    content,
    media,
    selectedBg,
    canShowBgPicker,
    taggedUsers,
    createPost,
    updatePost,
    t,
    onClose,
  ]);

  // Handle files selected from file input
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      const file = files[0]; // Single media per post

      const result = await upload(file, 'post');
      if (result) {
        let mediaType: 'image' | 'video' | 'document' = 'document';
        if (result.type === 'image') mediaType = 'image';
        else if (result.type === 'video') mediaType = 'video';

        setMedia({
          url: result.url,
          type: mediaType,
          fileName: file.name,
          fileSize: result.size,
          mimeType: result.mimeType,
        });
      }
    },
    [upload],
  );

  const handleRemoveMedia = useCallback(() => {
    setMedia(null);
    resetUpload();
  }, [resetUpload]);

  // Insert emoji at cursor position
  const handleEmojiSelect = useCallback((emoji: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent =
        content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);
      // Restore cursor position after emoji
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      });
    } else {
      setContent((prev) => prev + emoji);
    }
  }, [content]);

  const handleComingSoon = useCallback(() => {
    toast(t('feed.comingSoon'), { icon: '\u{1F6A7}' });
  }, [t]);

  const isSubmitDisabled = isPending || isAnimating || isUploading;

  if (!isOpen) return null;

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? t('feed.edit_post') : t('feed.create_post')}
        headerStyle={{
          background: 'linear-gradient(180deg, #0d2b6b 0%, #1a4fa0 40%, #3b8dd4 75%, #6bb4e8 100%)',
          color: '#ffffff',
          borderBottom: 'none',
        }}
        isPending={isPending || isUploading}
        isAnimating={isAnimating}
        onSubmit={handleSubmit}
        onValidate={validateForm}
        isFormValid={isFormValid}
        modalContentRef={modalContentRef}
        submitLabel={isEditing ? t('feed.update') : t('feed.publish')}
        error={error}
      >
        <div className="space-y-3">
          {/* User identity header — avatar + name */}
          <div className="flex items-center gap-3">
            <UserAvatar
              src={user?.avatarUrl}
              name={fullName}
              size="md"
              className="border border-slate-300/30"
            />
            <div className="min-w-0">
              <p className="text-sm font-bold" style={{ color: MODAL_COLORS.text }}>
                {fullName}
              </p>
              {taggedUsers.length > 0 && (
                <p className="text-xs" style={{ color: '#65676B' }}>
                  {t('feed.isWith')}{' '}
                  {taggedUsers.length === 1 ? (
                    <span className="font-semibold" style={{ color: MODAL_COLORS.text }}>
                      {[taggedUsers[0].firstName, taggedUsers[0].lastName].filter(Boolean).join(' ')}
                    </span>
                  ) : taggedUsers.length === 2 ? (
                    <>
                      <span className="font-semibold" style={{ color: MODAL_COLORS.text }}>
                        {[taggedUsers[0].firstName, taggedUsers[0].lastName].filter(Boolean).join(' ')}
                      </span>
                      {' '}{t('feed.and')}{' '}
                      <span className="font-semibold" style={{ color: MODAL_COLORS.text }}>
                        {[taggedUsers[1].firstName, taggedUsers[1].lastName].filter(Boolean).join(' ')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold" style={{ color: MODAL_COLORS.text }}>
                        {[taggedUsers[0].firstName, taggedUsers[0].lastName].filter(Boolean).join(' ')}
                      </span>
                      {', '}
                      <span className="font-semibold" style={{ color: MODAL_COLORS.text }}>
                        {[taggedUsers[1].firstName, taggedUsers[1].lastName].filter(Boolean).join(' ')}
                      </span>
                      {' '}{t('feed.and_others', { count: taggedUsers.length - 2 })}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Content textarea */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('feed.whatsOnYourMind', { name: firstName })}
              rows={5}
              maxLength={MAX_CONTENT_LENGTH}
              className="w-full resize-none rounded-xl p-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              style={{
                backgroundColor: MODAL_COLORS.interactive,
                border: `1px solid ${MODAL_COLORS.border}`,
                color: MODAL_COLORS.text,
              }}
              dir="auto"
              disabled={isSubmitDisabled}
            />

            {/* Emoji button — bottom-end corner of textarea */}
            <button
              type="button"
              onClick={() => setIsEmojiPickerOpen((v) => !v)}
              disabled={isSubmitDisabled}
              className="absolute bottom-3 end-3 p-1.5 rounded-full transition-all active:scale-90 disabled:opacity-40"
              style={{
                color: isEmojiPickerOpen ? MODAL_COLORS.header : '#65676B',
              }}
              aria-label={t('feed.emoji')}
            >
              <Smile className="h-5 w-5" />
            </button>
          </div>

          {/* Background picker + character count row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Background picker — visible only for short text-only posts */}
              {canShowBgPicker && (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedBg(null)}
                    className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center font-bold text-[11px] transition-transform active:scale-95"
                    style={{
                      border: `2px solid ${!selectedBg ? '#1877F2' : MODAL_COLORS.border}`,
                      backgroundColor: !selectedBg ? '#EBF5FF' : MODAL_COLORS.interactive,
                      color: MODAL_COLORS.text,
                    }}
                  >
                    Aa
                  </button>
                  {STATUS_GRADIENTS.map((bg) => {
                    const isActive = selectedBg === bg.id;
                    return (
                      <button
                        key={bg.id}
                        type="button"
                        onClick={() => setSelectedBg(bg.id)}
                        className="w-8 h-8 shrink-0 rounded-lg transition-transform active:scale-95"
                        style={{
                          background: gradientCSS(bg),
                          boxShadow: isActive
                            ? '0 0 0 2px #FFFFFF, 0 0 0 3px #1877F2'
                            : 'none',
                          border: isActive ? 'none' : '1px solid rgba(0,0,0,0.1)',
                        }}
                      />
                    );
                  })}
                </>
              )}
            </div>

            {/* Character count */}
            <span
              className="text-xs font-semibold"
              style={{
                color:
                  content.length > MAX_CONTENT_LENGTH * 0.9
                    ? '#ef4444'
                    : MODAL_COLORS.text,
                opacity:
                  content.length > MAX_CONTENT_LENGTH * 0.9 ? 1 : 0.4,
              }}
            >
              {content.length}/{MAX_CONTENT_LENGTH}
            </span>
          </div>

          {/* Live preview for gradient background */}
          {selectedBg && (() => {
            const g = getGradientById(selectedBg);
            if (!g) return null;
            const previewColor = g.darkText ? '#050505' : '#FFFFFF';
            return (
              <div
                className="rounded-lg flex items-center justify-center min-h-[120px] px-6 text-center"
                style={{ background: gradientCSS(g) }}
              >
                <p
                  className="font-bold leading-tight drop-shadow-sm whitespace-pre-wrap break-words"
                  style={{ fontSize: '24px', lineHeight: '28px', color: previewColor }}
                >
                  {content || t('feed.post_placeholder')}
                </p>
              </div>
            );
          })()}

          {/* Upload progress */}
          {isUploading && (
            <div
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{
                backgroundColor: MODAL_COLORS.interactive,
                border: `1px solid ${MODAL_COLORS.border}`,
              }}
            >
              <Loader2
                className="h-5 w-5 animate-spin flex-shrink-0"
                style={{ color: MODAL_COLORS.header }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: `${MODAL_COLORS.border}20` }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${progress}%`,
                      backgroundColor: MODAL_COLORS.header,
                    }}
                  />
                </div>
                <span
                  className="text-xs font-bold mt-1 block"
                  style={{ color: MODAL_COLORS.text, opacity: 0.7 }}
                >
                  {t('feed.uploading')} {progress}%
                </span>
              </div>
            </div>
          )}

          {/* Media preview */}
          {media && !isUploading && (
            <div className="relative">
              {media.type === 'image' ? (
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: `1px solid ${MODAL_COLORS.border}` }}
                >
                  <img
                    src={media.url}
                    alt={t('feed.media_preview')}
                    className="w-full max-h-[200px] object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              ) : media.type === 'video' ? (
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: `1px solid ${MODAL_COLORS.border}` }}
                >
                  <video
                    src={media.url}
                    controls
                    preload="metadata"
                    className="w-full max-h-[200px]"
                  />
                </div>
              ) : (
                /* Document preview */
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: `1px solid ${MODAL_COLORS.border}` }}
                >
                  <FileTypeIcon
                    fileName={media.fileName ?? 'file'}
                    fileSize={media.fileSize}
                    url={media.url}
                  />
                </div>
              )}

              {/* Remove button */}
              <button
                type="button"
                onClick={handleRemoveMedia}
                disabled={isSubmitDisabled}
                className="absolute top-2 end-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 disabled:opacity-40"
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: `1px solid ${MODAL_COLORS.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* ═══ "Add to your post" action bar ═══ */}
          <div
            className="flex items-center justify-between rounded-xl px-4 py-3"
            style={{
              border: `1px solid ${MODAL_COLORS.border}`,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <span
              className="text-sm font-bold"
              style={{ color: MODAL_COLORS.text }}
            >
              {t('feed.addToPost')}
            </span>

            <div className="flex items-center gap-1">
              {/* Photo/Video */}
              <button
                type="button"
                onClick={() => {
                  setIsEmojiPickerOpen(false);
                  fileInputRef.current?.click();
                }}
                disabled={isSubmitDisabled || !!media}
                className="p-2 rounded-lg transition-all active:scale-90 disabled:opacity-40 cursor-pointer"
                aria-label={t('feed.photoVideo')}
              >
                <ImagePlay className="h-6 w-6" style={{ color: '#45BD62' }} />
              </button>

              {/* Tag People */}
              <button
                type="button"
                onClick={() => setIsTagModalOpen(true)}
                disabled={isSubmitDisabled}
                className="p-2 rounded-lg transition-all active:scale-90 disabled:opacity-40 cursor-pointer"
                aria-label={t('feed.tagPeople')}
                style={{
                  backgroundColor: taggedUsers.length > 0 ? '#1877F215' : undefined,
                }}
              >
                <UserRoundPlus className="h-6 w-6" style={{ color: '#1877F2' }} />
              </button>

              {/* Feeling/Activity */}
              <button
                type="button"
                onClick={handleComingSoon}
                disabled={isSubmitDisabled}
                className="p-2 rounded-lg transition-all active:scale-90 disabled:opacity-40 cursor-pointer"
                aria-label={t('feed.feelingActivity')}
              >
                <Smile className="h-6 w-6" style={{ color: '#F7B928' }} />
              </button>

              {/* Check-in */}
              <button
                type="button"
                onClick={handleComingSoon}
                disabled={isSubmitDisabled}
                className="p-2 rounded-lg transition-all active:scale-90 disabled:opacity-40 cursor-pointer"
                aria-label={t('feed.checkIn')}
              >
                <MapPin className="h-6 w-6" style={{ color: '#F5533D' }} />
              </button>
            </div>
          </div>

          {/* Hidden file input — inside modal to avoid z-index issues */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                handleFilesSelected(Array.from(files));
              }
              // Reset so the same file can be re-selected
              e.target.value = '';
            }}
          />
        </div>
      </FormModal>

      {/* EmojiPicker overlay */}
      <EmojiPicker
        isOpen={isEmojiPickerOpen}
        onClose={() => setIsEmojiPickerOpen(false)}
        onEmojiSelect={handleEmojiSelect}
        bottomOffset={80}
      />

      {/* TagPeopleModal */}
      <TagPeopleModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        initialTagged={taggedUsers}
        onDone={setTaggedUsers}
      />
    </>
  );
}
